import { Link } from "@tanstack/react-router";
import { ASSETS } from "#/lib/assets";
import { ROUTES } from "#/lib/routes";
import type { ShopSearch } from "#/lib/shop-search";
import {  SectionHead } from "../primitives.js";

interface CatTile {
  id: string;
  label: string;
  img: string;
  to?: string;
  search?: Partial<ShopSearch>;
  disabled?: boolean;
}

const TILES: readonly CatTile[] = [
  {
    id: "childrens",
    label: "Children's\nBooks",
    img: ASSETS.categories.childrens,
    search: { publisher: ["tyndale-house-publishers", "tahanan-books"]  },
  },
  {
    id: "novels",
    label: "Novels",
    img: ASSETS.categories.novels,
    search: { publisher: ["total-language-plus", "living-book-press"] },
  },
  {
    id: "textbooks",
    label: "Textbooks",
    img: ASSETS.categories.textbooks,
    search: { category: ["physical-books"] },
  },
  {
    id: "filipino",
    label: "Filipino\nBooks",
    img: ASSETS.categories.filipino,
    search: { subject: ["filipino"] },
  },
  {
    id: "resource",
    label: "Resource\nBooks",
    img: ASSETS.categories.resource,
    search: { category:["resource-books"] },
  },
  {
    id: "living",
    label: "Living\nBooks",
    img: ASSETS.categories.living,
    to: ROUTES.livingBooks,
  },
  {
    id: "ebooks",
    label: "Ebooks",
    img: ASSETS.categories.ebooks,
    search: { category: ["ebook"] },
  },
  {
    id: "merch",
    label: "Merch",
    img: ASSETS.categories.merch,
    search: { category: ["merchandise"] },
  },
  {
    id: "online",
    label: "Online",
    img: ASSETS.categories.online,
    search: { category: ["online-materials"] },
  },
  {
    id: "supplies",
    label: "Supplies",
    img: ASSETS.categories.supplies,
    search: { publisher: ["faber-castell"] },
  },
  {
    id: "bundles",
    label: "Curriculum\nBundles",
    img: ASSETS.categories.bundles,
    search: { category: ["bundles"] },
  },
  {
    id: "bju",
    label: "BJU Sets",
    img: ASSETS.categories.bju,
    search: {category: ["bju-sets"]}
  },
];

export function Categories() {
  return (
    <section className="pt-16 md:pt-40 md:pb-24">
      <div className="mx-auto w-full max-w-6xl px-6 md:px-10">
        <SectionHead className="text-3xl md:text-4xl text-center font-bold tracking-wider uppercase text-[#6F6E6E] border-b-3 border-[#89AC7E] pb-8 md:pb-14 mb-16 md:mb-24">
          CATEGORIES
        </SectionHead>
        
        <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-x-4 gap-y-10 md:gap-x-8 md:gap-y-16 justify-items-center">
          {TILES.map((c) => (
            <Tile key={c.id} cat={c} />
          ))}
        </div>
      </div>
    </section>
  );
}

const TILE_BASE = "flex w-full flex-col items-center gap-2 group text-center max-w-[130px]";

function Tile({ cat }: { cat: CatTile }) {
  const innerHtml = (isHoverable: boolean) => (
    <>
      <div
        style={{ backgroundImage: `url(${cat.img})` }}
         className={`size-[84px] sm:size-[96px] md:size-[122px] shrink-0 rounded-full bg-center bg-cover border-5 border-transparent transition-colors duration-150 ${
          isHoverable ? "group-hover:border-[#45553F]" : ""
        }`}
      />
      <span className="whitespace-pre-line font-medium text-xs sm:text-sm md:text-base text-lp-green-dk leading-tight mt-4 md:mt-8">
        {cat.label}
      </span>
    </>
  );

  if (cat.disabled) {
    return (
      <div
        className={`${TILE_BASE} cursor-not-allowed opacity-50`}
        aria-disabled="true"
        title="Coming soon"
      >
        {innerHtml(false)}
      </div>
    );
  }

  if (cat.search) {
    return (
      <Link
        to="/shop"
        search={{ ...cat.search, page: 1, sort: "default" }}
         className={`${TILE_BASE} cursor-pointer`}
      >
          {innerHtml(true)}
      </Link>
    );
  }

  return (
     <Link to={cat.to ?? ROUTES.shop} className={`${TILE_BASE} cursor-pointer`}>
      {innerHtml(true)}
    </Link>
  );
}
